import { type ComponentProps, Fragment, memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

import { type MentionsData } from '~/components/DiscussionsV2/DiscussionForm';
import { UserMention } from '~/components/DiscussionsV2/UserMention/UserMention';
import Linkify from '~/components/Linkify';
import { useExternalLinksClickableContext } from '~/providers/ExternalLinksClickableProvider';
import { uuidRegExpString } from '~/utils/Url';

// Captures mentions in the style of '@[First Last](UUID)'
// Same as react-mentions, this pattern matching assumes no parentheses and
// brackets in user names.
const MENTION_REG_EXP = /(@\[[^()]*\]\([^()]*\))/;
const MENTION_NAME_REG_EXP = /\[(.*)\]/;
const MENTION_ID_REG_EXP = new RegExp(`\\((${uuidRegExpString})\\)`, 'i');

const NAMED_LINK_REG_EXP = /(\[[^\]]+\]\(https?:\/\/[^()]*(?:\([^()]*\)[^()]*)*\))/;
const NAMED_LINK_PARTS_REG_EXP = /\[([^\]]+)\]\((https?:\/\/[^()]*(?:\([^()]*\)[^()]*)*)\)/;

export type CommentDisplayProps = Pick<ComponentProps<'div'>, 'className'> & {
  text: string;
  'data-testid'?: string;
  metadata?: ReactNode;
  mentionsList?: MentionsData[];
};

export const CommentDisplay = memo(
  ({ className, text, 'data-testid': testId, metadata, mentionsList = [] }: CommentDisplayProps) => {
    const { areExternalLinksClickable } = useExternalLinksClickableContext();

    return (
      <Linkify
        disable={!areExternalLinksClickable}
        // biome-ignore lint/performance/noJsxPropsBind: each linkified anchor needs target and rel added to the props Linkify passes in
        renderLink={(props) => <a {...props} target="_blank" rel="noreferrer" />}
      >
        <div
          className={cn('whitespace-pre-wrap ads:text-secondary text-14 text-gray-11', className)}
          data-testid={testId}
        >
          {text.split(MENTION_REG_EXP).flatMap((str, index) => {
            if (index === 0 && metadata) {
              return [
                <Fragment key={index}>
                  {metadata}
                  {str.split(NAMED_LINK_REG_EXP).map((part, partIndex) => {
                    const match = part.match(NAMED_LINK_PARTS_REG_EXP);

                    if (match) {
                      const [, linkText, linkUrl] = match;

                      if (!areExternalLinksClickable) {
                        return <Fragment key={partIndex}>{linkText}</Fragment>;
                      }

                      return (
                        <a key={partIndex} href={linkUrl} target="_blank" rel="noreferrer">
                          {linkText}
                          <span className="sr-only">(opens in new tab)</span>
                        </a>
                      );
                    }

                    return (
                      <span key={partIndex} className="ads:text-primary text-14 text-gray-12">
                        {part}
                      </span>
                    );
                  })}
                </Fragment>,
              ];
            }
            if (str.match(MENTION_REG_EXP)) {
              const userName = str.match(MENTION_NAME_REG_EXP)?.[1] ?? 'Anonymous';
              const userId = str.match(MENTION_ID_REG_EXP)?.[1];

              const mentionedUser = mentionsList.find(({ id }) => id === userId);

              return [<UserMention key={index} userName={userName} user={mentionedUser} />];
            }

            return str.split(NAMED_LINK_REG_EXP).map((part, partIndex) => {
              const match = part.match(NAMED_LINK_PARTS_REG_EXP);

              if (match) {
                const [, linkText, linkUrl] = match;

                if (!areExternalLinksClickable) {
                  return <Fragment key={`${index}-${partIndex}`}>{linkText}</Fragment>;
                }

                return (
                  <a key={`${index}-${partIndex}`} href={linkUrl} target="_blank" rel="noreferrer">
                    {linkText}
                    <span className="sr-only">(opens in new tab)</span>
                  </a>
                );
              }

              return part;
            });
          })}
        </div>
      </Linkify>
    );
  },
);

CommentDisplay.displayName = 'CommentDisplay';
